/* eslint-disable @typescript-eslint/naming-convention */
import { clientInfo } from '@meitu/pc-bridge';
import { ROBONEO_PC_AI_WATERMARK, ROBONEO_WEB_AI_WATERMARK } from '@meitu/xdesign-widgets/dist/AIWatermark/constants';
import { drawLoadedAIWatermarkOnCanvas } from '@meitu/xdesign-widgets/dist/AIWatermark/utils';
import aiPcWatermarkRoboNeo from '@/assets/ai-cn-watermark-pc.png';
import aiCnWatermark from '@/assets/ai-cn-watermark.png';
import { loadImageAsync } from '@/utils/file-kit';
import { traverseTree } from '@/utils/tree-kit';
import { MAX_STAGE_HEIGHT, MAX_STAGE_WIDTH, type ILayerProps } from '@/version-types';
import type { TBBox } from '../../shared';
import type { ServiceManager } from '../service-manager';
import { EdgelessBoundingBox } from './bounding-box';

export type THighlightArea = { x: number; y: number; width: number; height: number };

export type TResource = HTMLCanvasElement | HTMLImageElement | HTMLVideoElement | ImageBitmap | null | void;

/** AI 水印图片 */
let aiWatermarkImage: HTMLImageElement | null = null;
/** 获取 AI 水印图片 */
async function loadAiWatermarkImage() {
    if (aiWatermarkImage) return aiWatermarkImage;
    aiWatermarkImage = await loadImageAsync(clientInfo.isClient ? aiPcWatermarkRoboNeo : aiCnWatermark, false);
    return aiWatermarkImage;
}
loadAiWatermarkImage().then(() => {
    console.log('aiWatermarkImage=>', { aiCnWatermark, aiWatermarkImage });
});

function isPromiseLike<T>(value: unknown): value is PromiseLike<T> {
    return !!value && typeof (value as PromiseLike<T>).then === 'function';
}

/** 水印选项 */
export interface IResultOptions {
    /** 是否渲染 ai 水印 */
    aiWatermark?: boolean;
    /** 缩放比例 */
    scale?: number;
}

interface IEdgelessObjectResultBase {
    /** SVG 资源，包含图层的透明度 */
    svg?: string;
    /** Markdown 文本资源 */
    markdown?: string;
    /** HTTP URL 资源 */
    url?: string;
}

/**
 * 图层导出结果。
 *
 * Canvas 与其舞台边界必须来自同一次导出，避免消费者把新资源放进旧 drawable 的边界。
 */
export type IEdgelessObjectResult =
    | (IEdgelessObjectResultBase & {
          /** 画布资源，不包含图层的透明度 */
          canvas: HTMLCanvasElement;
          /** 与本次画布资源同源的舞台边界 */
          renderBox: TBBox;
      })
    | (IEdgelessObjectResultBase & {
          /** 画布资源，不包含图层的透明度 */
          canvas?: undefined;
          /** 无画布时不应单独返回渲染边界 */
          renderBox?: undefined;
      });

/**
 * 规范化图层结果缩放比例。
 * @param scale - 外部传入的缩放比例
 * @returns 有效缩放比例
 */
export function normalizeResultScale(scale = 1) {
    return Number.isFinite(scale) && scale > 0 ? scale : 1;
}

export abstract class EdgelessObject extends EdgelessBoundingBox {
    private __loadResourceTimer: NodeJS.Timeout | null = null;
    private __loadResourceVersion = 0;

    /** 是否不渲染 */
    readonly noRender: boolean = false;
    /** 是否通过 dom 渲染 */
    readonly domRender: boolean = false;

    /** 是否渲染 ai 水印 */
    __renderAiWatermark: boolean = false;

    layerId: string;
    /** 创建渲染对象时的图层类型，用于识别保留 ID 的跨类型替换 */
    layerType?: ILayerProps['layerType'];
    readonly service: ServiceManager;

    resource: HTMLCanvasElement | HTMLImageElement | HTMLVideoElement | ImageBitmap | null = null;

    visible: boolean = true;
    opacity: number = 1;
    private __renderHidden = false;

    /** 临时渲染隐藏状态，不写入图层数据，也不随位置更新重置 */
    get renderHidden() {
        return this.__renderHidden;
    }

    /**
     * 临时隐藏画布对象并请求重绘，供拖拽 DOM 预览接管显示
     *
     * @param hidden - 是否临时隐藏；交互结束时须恢复为 false
     */
    setRenderHidden(hidden: boolean) {
        if (this.__renderHidden === hidden) return;
        this.__renderHidden = hidden;
        this.service.edgelessCanvas.setDirty({ object: this });
    }

    /** 聚焦信息 */
    focusInfo?: {
        /** 聚焦遮罩颜色 */
        maskRgba?: string;
        /** 聚焦高亮区域 */
        highlightArea?: THighlightArea;
    };

    constructor(layerId: string, service: ServiceManager, noRender: boolean = false, domRender: boolean = false) {
        super();
        this.layerId = layerId;
        this.service = service;
        this.noRender = noRender;
        this.domRender = domRender;
    }

    /*
     * 创建对象
     * @param layer - 图层
     * @param service - 服务
     * @returns - 对象
     */
    static create(layer: ILayerProps, service: ServiceManager) {
        const obj = service.behavior.get(layer)?.getEdgelessObject<EdgelessObject>?.(layer);
        if (obj) {
            obj.layerType = layer.layerType;
            return obj;
        }
        return null;
    }

    /**
     * 获取图层资源缩放比例。
     *
     * naturalWidth/naturalHeight 用于限制原始资源像素，logicalWidth/logicalHeight 用于结果外扩边界，
     * targetScale 用于限制目标缩放比例，layerSize 用于尚未写入 store 的图层草稿尺寸。
     *
     * @param options - 资源像素上限、逻辑边界、目标倍率及可选的图层尺寸
     * @returns 满足资源像素上限和目标倍率限制的缩放比例
     */
    getResourceScale({
        naturalWidth = 0,
        naturalHeight = 0,
        logicalWidth,
        logicalHeight,
        targetScale,
        layerSize,
    }: {
        naturalWidth?: number;
        naturalHeight?: number;
        logicalWidth?: number;
        logicalHeight?: number;
        targetScale?: number;
        layerSize?: Pick<ILayerProps, 'width' | 'height'>;
    } = {}) {
        const retinaScaling = this.service.edgelessCanvas.retinaScaling;
        const layer = layerSize ?? this.service.flatLayer.getLayer(this.layerId);
        const { width: canvasWidth, height: canvasHeight } = this.service.frame.getSize(
            this.service.frame.getLayerFrameId(this.layerId)!,
        );
        const width = Math.max(1, logicalWidth ?? layer.width);
        const height = Math.max(1, logicalHeight ?? layer.height);
        const maxWidth = Math.max(
            1,
            Math.min(naturalWidth || canvasWidth || MAX_STAGE_WIDTH, Math.floor(layer.width * retinaScaling)),
        );
        const maxHeight = Math.max(
            1,
            Math.min(naturalHeight || canvasHeight || MAX_STAGE_HEIGHT, Math.floor(layer.height * retinaScaling)),
        );
        const resourceScale = Math.min(maxWidth / width, maxHeight / height);

        return targetScale === undefined ? resourceScale : Math.min(normalizeResultScale(targetScale), resourceScale);
    }

    /**
     * 获取图层结果
     * @returns - 图层结果
     */
    abstract getResult(args?: IResultOptions): Promise<IEdgelessObjectResult>;

    /** 是否支持返回 Canvas 结果。 */
    supportsCanvasResult() {
        return false;
    }

    /** 是否支持返回 SVG 结果。 */
    supportsSvgResult() {
        return false;
    }

    /** 是否支持返回 Markdown 结果。 */
    supportsMarkdownResult() {
        return false;
    }

    /** 是否支持返回 URL 结果。 */
    supportsUrlResult() {
        return false;
    }

    /**
     * 设置是否渲染 ai 水印
     * @param isRender - 是否渲染
     */
    setAiWatermark(isRender: boolean) {
        const layer = this.service.flatLayer.getLayer(this.layerId);
        if (isRender !== this.__renderAiWatermark && layer.__renderAiWatermark) {
            this.__renderAiWatermark = isRender;
            this.runAfterLoadResource(() => {
                this.service.edgelessCanvas.setDirty({ object: this });
            });
        }
    }

    /**
     * 异步绘制图层 AI 水印信息
     * @param canvas - 画布
     * @param forceDraw - 是否强制绘制
     */
    async drawAiWatermarkAsync(canvas: HTMLCanvasElement, forceDraw = false, hasEffect = false) {
        if (forceDraw || this.__renderAiWatermark) {
            await loadAiWatermarkImage();
        }
        this.drawAiWatermark(canvas, forceDraw, hasEffect);
    }

    /**
     * 同步绘制图层 AI 水印
     * @param canvas - 画布
     * @param forceDraw - 是否强制绘制
     */
    drawAiWatermark(canvas: HTMLCanvasElement, forceDraw = false, hasEffect = false) {
        this.__drawAiWatermark(canvas, forceDraw, hasEffect);
    }

    /**
     * 同步绘制 ai 水印
     * @param canvas - 画布
     * @param forceDraw - 是否强制绘制
     */
    private __drawAiWatermark(canvas: HTMLCanvasElement, forceDraw = false, hasEffect = false) {
        if (!forceDraw && !this.__renderAiWatermark) return;
        if (!aiWatermarkImage) return;
        // 特效会在原图四周增加留白，角标仍锚定于原图区域。
        const width = hasEffect ? canvas.width / 1.4 : canvas.width;
        const height = hasEffect ? canvas.height / 1.4 : canvas.height;
        const asset = clientInfo.isClient ? ROBONEO_PC_AI_WATERMARK : ROBONEO_WEB_AI_WATERMARK;
        drawLoadedAIWatermarkOnCanvas(canvas, aiWatermarkImage, asset, {
            opacity: 1,
            imageRect: {
                x: (canvas.width - width) / 2,
                y: (canvas.height - height) / 2,
                width,
                height,
            },
        });
    }

    /**
     * 可以重写这个方法，在渲染之前重置资源
     * 比如：播放视频时，重置资源为视频，播放gif时，重置资源为gif帧资源
     * this.resource = 当前帧资源
     */
    resetResource(): void {}

    /**
     * 常规画布是否跳过渲染；聚焦态由 FocusPlugin 绘制。
     * @returns - 是否跳过渲染
     */
    shouldSkipRender(): boolean {
        return !!this.focusInfo;
    }

    /**
     * FocusPlugin 是否跳过渲染，可由子类根据自身状态重写。
     * @returns - 是否跳过渲染
     */
    shouldSkipFocusRender(): boolean {
        return false;
    }

    /**
     * 图层获得焦点
     * 可以重新实现这个方法，在图层获得焦点时做一些工作
     * 例如：gif 图层需要自动播放
     * @returns - 图层对象
     */
    active(): EdgelessObject | null {
        return null;
    }

    /**
     * 图层失去焦点，要触发这个方法，需要重写 active() 返回 this，否则不会触发
     * 可以重新实现这个方法，在图层失去焦点时做一些工作
     * 例如：gif 图层需要自动暂停
     * @returns - 图层对象
     */
    inactive(): EdgelessObject | null {
        return null;
    }

    /**
     * 销毁对象
     * 可以重写这个方法，在销毁对象时做一些清理工作
     */
    dispose() {}

    /**
     * 创建资源（子类实现）
     * 返回资源对象则更新 this.resource，返回 undefined 则不更新 resource
     */
    abstract loadResource(): Promise<TResource> | TResource;

    /**
     * 控制初始化资源的加载时机，子类可覆盖以保留同步启动行为
     *
     * @returns 默认 true 表示通过 rAF 分帧加载，false 表示初始化时直接启动
     */
    get useRAF(): boolean {
        return true;
    }

    /**
     * 最新一轮资源加载完成并通过版本校验后触发。
     *
     * @param resource - 绘制资源
     */
    protected onResourceSettled(_: TResource): void {}

    /** 使之前发起的资源请求和延迟加载失效，供交互预览接管期间阻止旧结果覆盖新几何信息 */
    protected invalidatePendingResource() {
        this.__loadResourceVersion += 1;
        if (this.__loadResourceTimer) {
            clearTimeout(this.__loadResourceTimer);
            this.__loadResourceTimer = null;
        }
    }

    /**
     * 加载资源（带版本控制，防止并发调用时旧结果覆盖新结果）
     * @returns 当前加载是否为最新有效的结果
     */
    private loadResourceWithVersion(): Promise<boolean> | boolean {
        const version = ++this.__loadResourceVersion;
        const result = this.loadResource();

        const applyResource = (resource: TResource) => {
            if (version !== this.__loadResourceVersion) return false;

            if (resource !== undefined) {
                this.resource = resource;
            }

            this.onResourceSettled(resource);
            return true;
        };

        return isPromiseLike(result) ? Promise.resolve(result).then(applyResource) : applyResource(result);
    }

    /*
     * 在资源加载完成后执行回调
     * @param callback - 回调函数
     * @param onFinally - 最终回调函数
     */
    private runAfterLoadResource(callback: () => void, onFinally?: () => void) {
        const handleLoaded = (isCurrent: boolean) => {
            if (!isCurrent) return;
            callback();
        };
        const result = this.loadResourceWithVersion();

        if (isPromiseLike(result)) {
            Promise.resolve(result)
                .then(handleLoaded)
                .finally(() => {
                    onFinally?.();
                });
            return;
        }

        handleLoaded(result);
        onFinally?.();
    }

    /**
     * 渲染
     */
    render(ctx: CanvasRenderingContext2D) {
        if (this.noRender || this.domRender || this.width <= 0 || this.height <= 0) return;
        this.resetResource();
        ctx.drawImage(this.resource || this.service.edgelessCanvas.placeholderCanvas, 0, 0, this.width, this.height);
        this.service.svipWatermark.drawLayerWatermark(this, ctx);
    }

    /** 声明图层内容是否需要在入场动画结束后持续重绘 */
    get requiresContinuousRender(): boolean {
        return false;
    }

    /**
     * 绘制导航预览，默认复用普通图层绘制，媒体图层可覆盖为封面绘制
     * @param ctx - 导航预览上下文，会写入图层像素
     */
    renderPreview(ctx: CanvasRenderingContext2D) {
        this.render(ctx);
    }

    /**
     * 获取左上角为中心点的基础信息
     * @returns - 左上角为中心点的基础信息
     */
    getBaseInfo(): TBBox {
        // 图层数据的中心点为图层中心点
        const layer = this.service.flatLayer.getLayer(this.layerId);
        const size = { width: layer.width, height: layer.height };
        // 转换为左上角中心点
        const newPosition = this.calcCenterIsLeftTopPosition({
            left: layer.left,
            top: layer.top,
            rotate: layer.rotate,
            ...size,
        });
        return {
            ...newPosition,
            ...size,
        };
    }

    /**
     * 更新
     * @param onlyUpdateBaseInfo - 是否只更新基础信息，基础信息包括：（left、top、width、height、opacity、rotate）
     */
    async update(onlyUpdateBaseInfo: boolean, isAdd = false) {
        const layer = this.service.flatLayer.getLayer(this.layerId);
        this.visible = !this.service.flatLayer.checkIsNeedHidden(layer);

        const updateBaseInfo = () => {
            this.visible = !this.service.flatLayer.checkIsNeedHidden(layer);
            this.opacity = layer.opacity;
            this.angle = layer.rotate;
            const baseInfo = this.getBaseInfo();
            this.left = baseInfo.left;
            this.top = baseInfo.top;
            this.width = baseInfo.width;
            this.height = baseInfo.height;
            this.setCoords();
        };

        this.__renderAiWatermark = layer.__renderAiWatermark ?? false;
        if (isAdd) {
            updateBaseInfo();
        }

        if (!onlyUpdateBaseInfo) {
            if (this.__loadResourceTimer) clearTimeout(this.__loadResourceTimer);
            if (!this.visible) {
                this.__loadResourceTimer = setTimeout(() => {
                    this.runAfterLoadResource(() => {
                        updateBaseInfo();
                        this.service.edgelessCanvas.setDirty({ object: this });
                    });
                }, 1000);
            } else {
                if (isAdd) {
                    this.service.loading.enter(this.layerId);
                }
                this.runAfterLoadResource(
                    () => {
                        updateBaseInfo();
                        this.service.edgelessCanvas.setDirty({ object: this, isAdd });
                    },
                    () => {
                        if (isAdd) {
                            this.service.loading.exit(this.layerId);
                        }
                    },
                );
            }
        } else {
            if (!isAdd) {
                updateBaseInfo();
                this.service.edgelessCanvas.setDirty({ object: this, onlyUpdateBaseInfo: true });
            }
        }
    }

    /** 退出聚焦 */
    exitFocus() {
        this.focusInfo = undefined;
    }

    /**
     * 进入聚焦
     * @param maskRgba - 遮罩颜色
     */
    setFocus(maskRgba: string, highlightArea?: THighlightArea) {
        if (!this.focusInfo) {
            this.focusInfo = {
                maskRgba,
                highlightArea,
            };
        } else {
            this.focusInfo.maskRgba = maskRgba;
            this.focusInfo.highlightArea = highlightArea;
        }
    }

    /**
     * 设置聚焦高亮区域
     * @param highlightArea - 高亮区域
     */
    setFocusHighlight(highlightArea?: THighlightArea) {
        if (this.focusInfo) {
            this.focusInfo.highlightArea = highlightArea;
        }
    }

    /**
     * 设置可见性
     * @param visible - 是否可见
     * @param rerender - 是否重新渲染
     */
    setVisible(visible: boolean) {
        if (this.visible === visible) return;
        const { flatLayer, frame, edgelessCanvas } = this.service;
        this.visible = visible;
        const layer = flatLayer.getLayer(this.layerId);
        const isHidden = !visible;
        traverseTree(layer, (item) => {
            flatLayer.setHidden(item, isHidden);
            frame.addOrUpdateLayer(item, undefined, true);
        });
        edgelessCanvas.requestRenderAll();
    }

    /** 检查对象是否在视口内 */
    checkInViewport(blindAreas?: { top?: number; right?: number; bottom?: number; left?: number }) {
        const canvas = this.service.edgelessCanvas;
        const translateX = canvas.translateX;
        const translateY = canvas.translateY;

        return this.getIntersectsInfo(
            canvas.zoom,
            canvas.size,
            {
                x: translateX,
                y: translateY,
            },
            blindAreas,
        );
    }

    /**
     * 获取中心点为左上角的对象在裁剪区内的相对位置信息
     * @param frameId - 帧 id
     * @returns - 中心点为左上角的对象在裁剪区内的相对位置信息
     */
    getPositionInFrame(frameId: number) {
        return this.__getPositionInFrame(frameId, this.getBaseInfo());
    }

    private __getPositionInFrame(frameId: number, baseInfo: TBBox) {
        if (!this.service.edgelessCanvas.isEdgelessMode) {
            return baseInfo;
        }
        const frame = this.service.frame.get(frameId);
        if (!frame) return null;
        const info = this.service.frame.getBaseInfo(frameId);
        return { left: baseInfo.left - info.left, top: baseInfo.top - info.top };
    }
}
